/* Agent Ordinary zero-cost analytics engine.
   All routines are pure, bounded, and side-effect free. No external actions. */
type Obj=Record<string,unknown>;
const finite=(n:unknown,label:string,min=-1e12,max=1e12):number=>{
 if(typeof n!=='number'||!Number.isFinite(n)||n<min||n>max)throw new RangeError('invalid '+label);
 return n;
};
const record=(v:unknown,label='input'):Obj=>{
 if(!v||typeof v!=='object'||Array.isArray(v))throw new TypeError(label+' must be an object');
 return v as Obj;
};
const rows=(v:unknown,max=2000):Obj[]=>{
 if(!Array.isArray(v)||v.length>max)throw new RangeError('invalid number of records');
 return v.map((a,i)=>record(a,'record '+i));
};
const txt=(v:unknown,label:string,max=300):string=>{
 if(typeof v!=='string'||!v.trim()||v.length>max)throw new RangeError('invalid '+label);
 return v.trim();
};
const prob=(v:unknown):number=>finite(v,'probability',0,1);
const fixed=(n:number)=>Math.round(n*1e8)/1e8;
const average=(a:number[])=>a.reduce((s,n)=>s+n,0)/(a.length||1);
const median=(a:number[])=>{const x=[...a].sort((p,q)=>p-q);return x.length?(x.length%2?x[(x.length-1)/2]:(x[x.length/2-1]+x[x.length/2])/2):null};
const dollar=(x:number)=>Math.round(x*100)/100;
export const cleanText=(v:unknown,max=200)=>txt(v,'text',max);
export const safeRecord=record;

export function oddsToDecimal(value:unknown,format:string='american'):number{
 const n=finite(value,'odds',-1000000,1000000);
 if(format==='decimal'){if(n<=1)throw new RangeError('decimal odds must be greater than 1');return n;}
 if(format==='probability'){if(n<=0||n>=1)throw new RangeError('implied probability 0..1 exclusive');return 1/n;}
 if(format!=='american'||Math.abs(n)<100)throw new RangeError('invalid American odds');
 return n>0?1+n/100:1+100/Math.abs(n);
}
export function oddsAnalysis(op:string,input:unknown){
 const d=record(input);
 if(op==='convert_odds'){
  const decimal=oddsToDecimal(d.odds,String(d.format||'american'));
  return {decimal:fixed(decimal),american:decimal>=2?fixed((decimal-1)*100):fixed(-100/(decimal-1)),
    impliedProbability:fixed(1/decimal)};
 }
 if(op==='devig'||op==='find_arbitrage'){
  const format=String(d.format||'american');
  const prices=Array.isArray(d.odds)?d.odds:d.marketProbs;
  if(!Array.isArray(prices)||prices.length<2||prices.length>30)throw new RangeError('2-30 prices required');
  const implied=prices.map(x=>op==='find_arbitrage'&&d.marketProbs?prob(x):1/oddsToDecimal(x,format));
  if(implied.some(x=>x<=0))throw new RangeError('invalid price');
  const sum=implied.reduce((a,b)=>a+b,0);
  if(op==='devig')return {vig:fixed(sum-1),overround:fixed(sum),
    fairProbabilities:implied.map(x=>fixed(x/sum)),rawProbabilities:implied.map(fixed),
    method:'multiplicative',labels:Array.isArray(d.labels)?d.labels:null};
  return {arbitrageFound:sum<1-1e-8,overround:fixed(sum),guaranteedRoi:sum<1?fixed(1/sum-1):0,
   allocationFractions:implied.map(x=>fixed(x/sum)),assumesExhaustiveMutuallyExclusiveOutcomes:true};
 }
 if(op==='find_edge'||op==='kelly_criterion'){
  const fair=prob(d.fairProbability),market=prob(d.marketProbability);
  if(market<=0||market>=1)throw new RangeError('market probability invalid');
  const net=1/market-1;
  const kelly=Math.max(0,(fair*(net+1)-1)/net);
  return {fairProbability:fair,marketProbability:market,edge:fixed(fair-market),
    evPerDollar:fixed(fair/market-1),kellyFraction:fixed(Math.min(1,kelly)),
    noWagerPlaced:true};
 }
 if(op==='parlay_analysis'){
  if(!Array.isArray(d.legs)||d.legs.length<2||d.legs.length>20)throw new RangeError('invalid leg count');
  const p=d.legs.map(prob).reduce((a,b)=>a*b,1);
  const decimal=oddsToDecimal(d.parlayOdds,String(d.format||'american'));
  const net=decimal-1;
  return {jointFairProbability:fixed(p),assumesIndependentLegs:true,evPerDollar:fixed(p*decimal-1),
    kellyFraction:fixed(Math.max(0,Math.min(1,(p*decimal-1)/net))),noWagerPlaced:true};
 }
 if(op==='line_movement'){
  const from=1/oddsToDecimal(d.openOdds,String(d.format||'american'));
  const to=1/oddsToDecimal(d.closeOdds,String(d.format||'american'));
  return {openingImpliedProbability:fixed(from),closingImpliedProbability:fixed(to),
    probabilityShift:fixed(to-from),direction:to>from?'shortened':to<from?'drifted':'unchanged',
    caution:'Odds movement alone cannot establish sharp action or causation.'};
 }
 if(op==='hedge_matrix'){
  const states=rows(d.states,80).map(x=>txt(x.id,'state id',80));
  if(!states.length||new Set(states).size!==states.length)throw new RangeError('exhaustive distinct states required');
  const tickets=rows(d.tickets,80);
  const newBets=rows(d.newBets,80);
  const previousStakes=tickets.reduce((sum,x)=>sum+finite(x.stake,'stake',0,1e9),0);
  const addedStakes=newBets.reduce((sum,x)=>sum+finite(x.stake,'stake',0,1e9),0);
  const netByState=states.map(state=>{
    const paid=(x:Obj)=>Array.isArray(x.winningStates)&&x.winningStates.includes(state)
      ?finite(x.stake,'stake',0,1e9)*oddsToDecimal(x.decimalOdds,'decimal'):0;
    const returns=tickets.reduce((sum,x)=>sum+paid(x),0)+newBets.reduce((sum,x)=>sum+paid(x),0);
    return {state,net:dollar(returns-previousStakes-addedStakes)};
  });
  const minNet=Math.min(...netByState.map(x=>x.net));
  return {strictProfitLock:minNet>0,minNet,netByState,
    omittedStateVerification:'Caller must list all mutually exclusive exhaustive outcomes.',
    noBetsPlaced:true};
 }
 throw new RangeError('unknown odds operation');
}

export function analyzeData(input:unknown){
 const d=record(input), data=rows(d.rows,3000);
 if(!data.length)throw new RangeError('no data');
 const numericColumns=Object.keys(data[0]).filter(k=>data.some(r=>typeof r[k]==='number'));
 const measure=txt(d.measure,'measure',100);
 if(!numericColumns.includes(measure))throw new RangeError('measure not present');
 const selected=data.filter(r=>typeof r[measure]==='number'&&Number.isFinite(r[measure] as number));
 if(!selected.length)throw new RangeError('no valid measurements');
 const vals=selected.map(r=>finite(r[measure],measure));
 const groupKey=typeof d.groupBy==='string'?d.groupBy:'';
 const groups:Record<string,{count:number;sum:number;mean:number}>={};
 if(groupKey){
  for(const row of selected){
   const key=String(row[groupKey]??'(missing)').slice(0,100);
   const g=groups[key]??(groups[key]={count:0,sum:0,mean:0});g.count++;g.sum+=finite(row[measure],measure);g.mean=g.sum/g.count;
  }
 }
 let correlation:null|number=null;
 if(typeof d.compareWith==='string'&&numericColumns.includes(d.compareWith)){
  const paired=selected.filter(r=>typeof r[d.compareWith as string]==='number');
  if(paired.length>=3){
   const x=paired.map(r=>r[measure] as number),y=paired.map(r=>r[d.compareWith as string] as number);
   const mx=average(x),my=average(y);
   const cov=x.reduce((a,n,i)=>a+(n-mx)*(y[i]-my),0);
   const sx=x.reduce((a,n)=>a+(n-mx)**2,0),sy=y.reduce((a,n)=>a+(n-my)**2,0);
   correlation=sx&&sy?fixed(cov/Math.sqrt(sx*sy)):null;
  }
 }
 return {records: data.length,validMeasurements: vals.length,measure,mean:fixed(average(vals)),
  sum:fixed(vals.reduce((a,b)=>a+b,0)),min:Math.min(...vals),max:Math.max(...vals),
  median:median(vals),byGroup:groups,correlation,source:'owner_authorized_records'};
}

export function investigateRecords(operation:string,input:unknown){
 const d=record(input);const records=rows(d.records,1500);
 const search=typeof d.query==='string'?d.query.toLowerCase().trim():'';
 if(operation==='find_information'||operation==='customer_research'||operation==='company_research'){
  if(!search)throw new RangeError('query required');
  const terms=search.split(/[^\p{L}\p{N}]+/u).filter(x=>x.length>1).slice(0,12);
  const matched=records.map((r,i)=>{
   const body=Object.entries(r).filter(([k])=>!/(secret|token|password|key)/i.test(k))
    .map(([k,v])=>k+': '+JSON.stringify(v).slice(0,2000)).join(' ').slice(0,7500);
   const low=body.toLowerCase();const score=terms.reduce((sum,t)=>sum+(low.includes(t)?1:0),0);
   return {id:String(r.id??i),score,excerpt:body.slice(0,450),
    sourceUrl:typeof r.sourceUrl==='string'?r.sourceUrl:null};
  }).filter(x=>x.score>0).sort((a,b)=>b.score-a.score).slice(0,30);
  return {query:search,totalRecords:records.length,matches:matched,source:'authorized_dataset',
    evidenceRequired:true};
 }
 if(operation==='deal_review'){
  const risk:unknown[]=[];
  const reviewed=records.map(r=>{
   const age=finite(r.daysInStage??0,'daysInStage',0,10000);
   const probability=prob(r.probability??0.5),amount=finite(r.amount??0,'amount',0);
   const flags=Array.isArray(r.riskFlags)?r.riskFlags.map(x=>String(x).slice(0,100)):[];
   if(age>45)flags.push('stalled_more_than_45_days');
   if(!r.nextStep)flags.push('missing_next_step');
   return {id:String(r.id??''),amount,weightedAmount:fixed(amount*probability),
    daysInStage:age,probability,riskFlags:flags,attention:flags.length>0};
  });
  return {deals:reviewed,atRisk:reviewed.filter(x=>x.attention),totalWeighted:fixed(reviewed.reduce((s,x)=>s+x.weightedAmount,0)),
    note:'Heuristics on supplied deal facts; not a verified close prediction.'};
 }
 if(operation==='pipeline_review'){
  const group:Record<string,{count:number;amount:number;weighted:number;stale:number}>={};
  for(const r of records){
   const stage=String(r.stage||'unknown').slice(0,50);
   const v=group[stage]??(group[stage]={count:0,amount:0,weighted:0,stale:0});
   const amount=finite(r.amount??0,'amount',0),probability=prob(r.probability??0.5);
   v.count++;v.amount+=amount;v.weighted+=amount*probability;
   if(finite(r.daysInStage??0,'daysInStage',0,9999)>45)v.stale++;
  }
  return {byStage:group,stages:Object.keys(group),totalDeals:records.length,
    note:'Based on owner-authorized records; CRM sync required for latest status.'};
 }
 if(operation==='competitor_research'){
  const competitors=records.map(r=>({id:String(r.id??''),name:String(r.name??''),price:typeof r.price==='number'?r.price:null,
    features:Array.isArray(r.features)?r.features.slice(0,35):[],
    evidence:String(r.sourceUrl??'not supplied')}));
  const features=[...new Set(competitors.flatMap(x=>x.features.map(String)))];
  return {competitors,featureMatrix:features.map(f=>({feature:f,
    includedBy:competitors.filter(x=>x.features.includes(f)).map(x=>x.name)})),
    source:'owner-authorized competitor records; live sources must be separately fetched'};
 }
 if(operation==='digest'){
  const days=finite(d.days??7,'days',1,90);
  const limit=Date.now()-days*86400000;
  const items=records.filter(x=>Date.parse(String(x.at??x.created_at??''))>=limit);
  return {days,eventCount:items.length,events:items.slice(0,100),generatedAt:new Date().toISOString(),
    source:'owner-authorized-records'};
 }
 if(operation==='source_organization'){
  const src=records.map(r=>({id:String(r.id??''),type:String(r.type??''),name:String(r.name??''),
    lastUpdated:String(r.updatedAt??'unknown'),connected:r.connected===true,priority:finite(r.priority??50,'priority',0,100)}));
  src.sort((a,b)=>b.priority-a.priority||a.id.localeCompare(b.id));
  return {sources:src,connectedCount:src.filter(x=>x.connected).length};
 }
 throw new RangeError('unknown research operation');
}

export function signalReview(input:unknown){
 const d=record(input),signals=rows(d.signals,500);
 const items=signals.map(r=>{
  const outcome=finite(r.realizedReturn,'return',-1e4,1e4);
  const guess=String(r.expectedDirection||'up');
  const correct=(guess==='up'&&outcome>0)||(guess==='down'&&outcome<0)||(guess==='flat'&&outcome===0);
  return {id:String(r.id??''),expectedDirection:guess,realizedReturn:outcome,correct,
    regime:String(r.regime??'unspecified'),postmortem:String(r.notes??'').slice(0,1000)};
 });
 return {n:items.length,hitRate:items.length?fixed(items.filter(x=>x.correct).length/items.length):null,
  averageRealizedReturn:items.length?fixed(average(items.map(x=>x.realizedReturn))):null,
  falsePositives:items.filter(x=>!x.correct).map(x=>x.id),items,
  noOrdersPlaced:true};
}
